/**
 * GameTypeProvider
 *
 * Conditionally mounts the per-game-type context provider matching the
 * currently-selected game's gameType. When no game is selected — or the game
 * type has no dedicated provider — children render without an extra provider.
 *
 * Switching games of different types unmounts the previous provider and
 * mounts the new one, which is intentional: per-game-type state (Marco Polo
 * events, FPR scoring failures, FPR Results snapshot) should not leak
 * across game-type boundaries.
 */

import { ReactNode } from 'react';
import { GameType } from '@swtorbit/shared-socket-events';
import { useGameContext } from './GameContext';
import { HnsProvider } from './HnsContext';
import { FprProvider } from './FprContext';
import { TrlProvider } from './TrlContext';
import { HnstProvider } from './HnstContext';

interface GameTypeProviderProps {
  children: ReactNode;
}

export function GameTypeProvider({ children }: GameTypeProviderProps) {
  const { selectedGame } = useGameContext();

  if (selectedGame?.gameType === GameType.HIDE_AND_SEEK) {
    return <HnsProvider>{children}</HnsProvider>;
  }

  if (selectedGame?.gameType === GameType.FP_RACE) {
    return <FprProvider>{children}</FprProvider>;
  }

  // Hide and Seek Teams shares Hide and Seek's Marco Polo and round events.
  if (selectedGame?.gameType === GameType.HIDE_AND_SEEK_TEAMS) {
    return <HnsProvider><HnstProvider>{children}</HnstProvider></HnsProvider>;
  }

  if (selectedGame?.gameType === GameType.TRAILBLAZERS) {
    return <TrlProvider>{children}</TrlProvider>;
  }

  return <>{children}</>;
}
